import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    IconButton,
    Typography,
    TextField,
    Button,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoadingDialogStore } from '@/store/loadingDialogStore';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import DaumPostcode from 'react-daum-postcode';
import { postProxyImg } from '@/services/mallUser.service';
import axios from 'axios';
import config from '@/config/config';
import { useUserStore, useAnonymousUserStore } from '@/store/userStore';

interface RegisterDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function RegisterDialog({ open, onClose }: RegisterDialogProps) {
    const navigate = useNavigate();
    const params = useParams();
    const { setIsLoadingOpen } = useLoadingDialogStore((state) => state.action);
    const { setIsOpen: setLoginDialogOpen } = useLoginFormDialogStore((state) => state.action);
    const anonymousAccessToken = useAnonymousUserStore((state) => state.accessToken);
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [filePreview, setFilePreview] = useState<string | null>(null);
    const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm();

    const handleClickClose = () => {
        onClose();
        reset();
    };

    const handleAddressComplete = (data: any) => {
        setValue('businessAddress', data.address);
        setValue('businessAddressDetail', '');
        setAddressModalOpen(false);
    };

    const handleFileUpload = async (file: File) => {
        // 파일 타입 검증
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('JPG, PNG, PDF 파일만 업로드 가능합니다.');
            return;
        }

        // 파일 크기 검증 (10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            toast.error('파일 크기는 10MB 이하여야 합니다.');
            return;
        }

        try {
            setIsLoadingOpen(true);

            const formData = new FormData();
            formData.append('upload', file);
            const request = await postProxyImg('flowing-web-resources', '', formData).then((r) => {
                if (r.status !== 200) throw r;
                return r;
            });
            const uploadedUrl = request.url[0];
            if (!uploadedUrl) {
                throw new Error('업로드된 파일 URL을 받지 못했습니다.');
            }

            setUploadedFile(file);
            setUploadedFileUrl(uploadedUrl);
            setValue('businessLicense', uploadedUrl);

            // 이미지 파일인 경우 썸네일 생성
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setFilePreview(e.target?.result as string);
                };
                reader.readAsDataURL(file);
            } else {
                setFilePreview(null);
            }
        } catch (error) {
            console.error('파일 업로드 오류:', error);
            toast.error('파일 업로드에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoadingOpen(false);
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileUpload(files[0]);
        }
    };

    const passwordRegister = register('password', {
        required: '비밀번호를 입력해주세요.',
        pattern: {
            value: /^[a-zA-Z0-9!@#$%^&*?]{4,20}$/,
            message: '영어, 숫자, 특수문자 (!@#$%^&*?) 만 입력 가능, 4~20자리',
        },
        minLength: {
            value: 4,
            message: '비밀번호는 최소 4글자 이상으로 입력 가능합니다.',
        },
        maxLength: {
            value: 20,
            message: '비밀번호는 최대 20글자 이하로 입력 가능합니다.',
        },
    });

    const phoneRegister = register('phone', {
        required: '휴대전화번호를 입력해주세요.',
        pattern: {
            value: /^[0-9]{10,11}$/,
            message: '올바른 휴대전화번호를 입력해주세요.',
        },
    });

    const nameRegister = register('name', {
        required: '닉네임을 입력해주세요.',
    });

    const emailRegister = register('email', {
        required: '이메일을 입력해주세요.',
        pattern: {
            value: /^([0-9a-zA-Z_\.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/,
            message: '올바른 이메일 형식을 입력해주세요.',
        },
    });

    const businessNumberRegister = register('businessNumber', {
        required: '사업자 번호를 입력해주세요.',
        pattern: {
            value: /^[0-9]{10}$/,
            message: '사업자 등록번호 숫자 10자리를 입력해주세요.',
        },
    });

    const companyNameRegister = register('companyName', {
        required: '상호명을 입력해주세요.',
    });

    const ceoNameRegister = register('ceoName', {
        required: '대표자명을 입력해주세요.',
    });

    const businessAddressRegister = register('businessAddress', {
        required: '사업자 주소를 입력해주세요.',
    });

    const businessAddressDetailRegister = register('businessAddressDetail', {
        required: '상세 주소를 입력해주세요.',
    });

    const businessLicenseRegister = register('businessLicense', {
        required: '사업자등록증을 업로드해주세요.',
    });

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClickClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                sx={{
                    zIndex: 1500,
                    '& .MuiDialog-paper': {
                        borderRadius: isMobile ? '0 !important' : '12px !important',
                        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        maxWidth: isMobile ? '100%' : '600px',
                        height: isMobile ? '100%' : 'auto',
                    },
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    {/* 모바일 뒤로가기 버튼 */}
                    {isMobile && (
                        <IconButton
                            onClick={handleClickClose}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                top: 16,
                                width: 40,
                                height: 40,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                zIndex: 10,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,1)',
                                },
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}

                    {/* PC 닫기 버튼 */}
                    {!isMobile && (
                        <IconButton
                            onClick={handleClickClose}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                zIndex: 1,
                                color: '#000',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}

                    <Box sx={{ p: { xs: 2, md: 4 }, pt: { xs: 4, md: 6 } }}>
                        {/* 제목 */}
                        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '24px', md: '28px' },
                                    color: '#222',
                                }}
                            >
                                회원가입
                            </Typography>
                        </Box>

                        <form
                            onSubmit={handleSubmit(
                                async (data) => {
                                    try {
                                        setIsLoadingOpen(true);

                                        const requestData = {
                                            nickname: data.name,
                                            email: data.email,
                                            phoneNumber: data.phone,
                                            password: data.password,
                                            imageUrl: '', // 프로필 이미지는 별도로 처리
                                            smsMarketingConsented: true,
                                            emailMarketingConsented: true,
                                            companyName: data.companyName,
                                            ownerName: data.ceoName,
                                            companyAddress:
                                                `${data.businessAddress} ${data.businessAddressDetail}`.trim(),
                                            businessRegistrationNumber: data.businessNumber,
                                            businessRegistrationCertificateUrl: uploadedFileUrl || '',
                                        };
                                        const response = await axios.post(
                                            `${config.api.studio_api}/users/sign-up`,
                                            requestData,
                                            {
                                                headers: {
                                                    Authorization: `Bearer ${anonymousAccessToken}`,
                                                },
                                            },
                                        );

                                        if (response.status === 200 || response.status === 201) {
                                            toast.success('회원가입이 완료되었습니다.');
                                            handleClickClose();
                                            setLoginDialogOpen(true);
                                        } else {
                                            throw new Error('회원가입에 실패했습니다.');
                                        }
                                    } catch (error: any) {
                                        if (error?.response?.status === 400) {
                                            error?.response?.data?.placeHolder &&
                                                toast.error(error?.response?.data?.placeHolder);
                                        } else {
                                            throw new Error('회원가입에 실패했습니다.');
                                        }
                                    } finally {
                                        setIsLoadingOpen(false);
                                    }
                                },
                                (errors) => {
                                    console.log('폼 유효성 검사 실패:', errors);
                                },
                            )}
                        >
                            {/* 회원 정보 입력 섹션 */}
                            <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '16px', md: '18px' },
                                        color: '#222',
                                        mb: { xs: 1.5, md: 2 },
                                    }}
                                >
                                    회원 정보 입력
                                </Typography>
                                <Divider sx={{ mb: { xs: 2, md: 3 } }} />

                                {/* 닉네임 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            닉네임
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...nameRegister}
                                        placeholder="닉네임을 입력하세요"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.name}
                                        helperText={String(errors.name?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 이메일 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            이메일
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...emailRegister}
                                        placeholder="@이하 주소까지 모두 입력"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.email}
                                        helperText={String(errors.email?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 비밀번호 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            비밀번호
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...passwordRegister}
                                        type="password"
                                        placeholder="영어, 숫자, 특수문자 (!@#$%^&*?) 만 입력 가능, 4~20자리"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.password}
                                        helperText={String(errors.password?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 휴대전화번호 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            휴대전화번호
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...phoneRegister}
                                        placeholder="휴대전화번호를 입력하세요"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.phone}
                                        helperText={String(errors.phone?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* 사업자 정보 입력 섹션 */}
                            <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '16px', md: '18px' },
                                        color: '#222',
                                        mb: { xs: 1.5, md: 2 },
                                    }}
                                >
                                    사업자 정보 입력
                                </Typography>
                                <Divider sx={{ mb: { xs: 2, md: 3 } }} />

                                {/* 사업자 번호 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            사업자 번호
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <TextField
                                            {...businessNumberRegister}
                                            placeholder="사업자 등록번호 숫자 10자리 (- 제외)"
                                            fullWidth
                                            variant="outlined"
                                            size="medium"
                                            error={!!errors.businessNumber}
                                            helperText={String(errors.businessNumber?.message || '')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#fff',
                                                    borderRadius: '4px',
                                                    '& fieldset': {
                                                        border: '1px solid #ddd',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#999',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF4B6B',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#000',
                                                    fontSize: '14px',
                                                    padding: '12px 16px',
                                                    '&:-webkit-autofill': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                        WebkitTextFillColor: '#000',
                                                    },
                                                    '&:-webkit-autofill:hover': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    },
                                                    '&:-webkit-autofill:focus': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* 상호명 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            상호명
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...companyNameRegister}
                                        placeholder="상호명을 입력하세요"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.companyName}
                                        helperText={String(errors.companyName?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 대표자명 */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            대표자명
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <TextField
                                        {...ceoNameRegister}
                                        placeholder="대표자명을 입력하세요"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.ceoName}
                                        helperText={String(errors.ceoName?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 사업자 주소 */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            사업자 주소
                                        </Typography>
                                        <Typography sx={{ color: '#FF4B6B', fontSize: '14px', ml: 0.5 }}>*</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 2 }}>
                                        <TextField
                                            {...businessAddressRegister}
                                            placeholder="주소"
                                            fullWidth
                                            variant="outlined"
                                            size="medium"
                                            disabled
                                            error={!!errors.businessAddress}
                                            helperText={String(errors.businessAddress?.message || '')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '4px',
                                                    '& fieldset': {
                                                        border: '1px solid #ddd',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#999',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#FF4B6B',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#000',
                                                    fontSize: '14px',
                                                    padding: '12px 16px',
                                                    '&:-webkit-autofill': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                        WebkitTextFillColor: '#000',
                                                    },
                                                    '&:-webkit-autofill:hover': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    },
                                                    '&:-webkit-autofill:focus': {
                                                        WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    },
                                                },
                                                '& .Mui-disabled': {
                                                    backgroundColor: '#f0f0f0',
                                                    '& .MuiInputBase-input': {
                                                        color: '#999',
                                                    },
                                                    '& fieldset': {
                                                        borderColor: '#ddd',
                                                    },
                                                },
                                            }}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={() => setAddressModalOpen(true)}
                                            sx={{
                                                minWidth: '120px',
                                                height: '45px',
                                                borderColor: '#ddd',
                                                color: '#999',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                backgroundColor: '#f0f0f0',
                                                '&:hover': {
                                                    borderColor: '#999',
                                                    backgroundColor: '#e8e8e8',
                                                },
                                            }}
                                        >
                                            주소 검색
                                        </Button>
                                    </Box>
                                    <TextField
                                        {...businessAddressDetailRegister}
                                        placeholder="상세 주소"
                                        fullWidth
                                        variant="outlined"
                                        size="medium"
                                        error={!!errors.businessAddressDetail}
                                        helperText={String(errors.businessAddressDetail?.message || '')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& fieldset': {
                                                    border: '1px solid #ddd',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#999',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF4B6B',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000',
                                                fontSize: '14px',
                                                padding: '12px 16px',
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                    WebkitTextFillColor: '#000',
                                                },
                                                '&:-webkit-autofill:hover': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                                '&:-webkit-autofill:focus': {
                                                    WebkitBoxShadow: '0 0 0 1000px #f5f5f5 inset',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* 증빙서류(사업자등록증) */}
                                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ color: '#222', fontSize: '14px', fontWeight: 500 }}>
                                            증빙서류(사업자등록증)
                                        </Typography>
                                    </Box>

                                    {/* 숨겨진 파일 입력 */}
                                    <input
                                        type="file"
                                        id="business-license-upload"
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        onChange={handleFileInputChange}
                                        style={{ display: 'none' }}
                                    />

                                    <Box
                                        onClick={() => document.getElementById('business-license-upload')?.click()}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        sx={{
                                            border: isDragOver ? '2px dashed #FF4B6B' : '2px dashed #ddd',
                                            borderRadius: '4px',
                                            p: 3,
                                            textAlign: 'center',
                                            backgroundColor: isDragOver ? '#fff5f5' : '#f5f5f5',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: '#999',
                                                backgroundColor: '#f0f0f0',
                                            },
                                        }}
                                    >
                                        {uploadedFile ? (
                                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                {/* 썸네일 또는 파일 정보 */}
                                                {filePreview ? (
                                                    <Box
                                                        component="img"
                                                        src={filePreview}
                                                        alt="업로드된 파일 썸네일"
                                                        sx={{
                                                            width: '120px',
                                                            height: '120px',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            border: '1px solid #ddd',
                                                            backgroundColor: '#fff',
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: '120px',
                                                            height: '120px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#f8f9fa',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '8px',
                                                            color: '#666',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        PDF
                                                    </Box>
                                                )}

                                                {/* X 버튼 */}
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setUploadedFile(null);
                                                        setFilePreview(null);
                                                        setUploadedFileUrl(null);
                                                        setValue('businessLicense', '');
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        right: -8,
                                                        width: '24px',
                                                        height: '24px',
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                        '&:hover': {
                                                            backgroundColor: '#333',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '14px',
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>
                                                    사업자등록증을 업로드해주세요
                                                </Typography>
                                                <Typography sx={{ color: '#999', fontSize: '12px' }}>
                                                    JPG, PNG, PDF 파일만 업로드 가능 (최대 10MB)
                                                </Typography>
                                                <Typography sx={{ color: '#999', fontSize: '11px', mt: 1 }}>
                                                    클릭하거나 파일을 드래그하여 업로드
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* 회원가입 버튼 */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                onClick={() => console.log('회원가입 버튼 클릭됨')}
                                sx={{
                                    backgroundColor: '#FF4B6B',
                                    color: '#fff',
                                    py: { xs: 2, md: 1.5 },
                                    fontSize: { xs: '18px', md: '16px' },
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#E03E5A',
                                    },
                                }}
                            >
                                회원가입
                            </Button>
                        </form>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* 주소검색 모달 */}
            <Dialog
                open={addressModalOpen}
                onClose={() => setAddressModalOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{
                    zIndex: 1600,
                    '& .MuiDialog-paper': {
                        borderRadius: isMobile ? '0 !important' : '12px !important',
                        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        maxWidth: isMobile ? '100%' : '500px',
                        height: isMobile ? '100%' : 'auto',
                    },
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    {/* 모바일 뒤로가기 버튼 */}
                    {isMobile && (
                        <IconButton
                            onClick={() => setAddressModalOpen(false)}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                top: 16,
                                width: 40,
                                height: 40,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                zIndex: 10,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,1)',
                                },
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}

                    {/* PC 닫기 버튼 */}
                    {!isMobile && (
                        <IconButton
                            onClick={() => setAddressModalOpen(false)}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                zIndex: 1,
                                color: '#000',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                    <Box sx={{ p: { xs: 1, md: 2 } }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '16px', md: '18px' },
                                color: '#222',
                                mb: { xs: 1.5, md: 2 },
                                textAlign: 'center',
                            }}
                        >
                            주소 검색
                        </Typography>
                        <DaumPostcode
                            onComplete={handleAddressComplete}
                            autoClose={false}
                            style={{
                                width: '100%',
                                height: isMobile ? 'calc(100vh - 120px)' : '400px',
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
